import type { BulkEditMeta } from '../../components/TanTable';
import { getDbFieldName } from '../../utils/dynamicColumns';
import { toDbName } from '../../hooks/orderFieldDbNames';

/**
 * Which Search Results columns can be edited in place, with what widget, and — the
 * part that matters most — what each edit is KEYED by.
 *
 * A field belongs to something, and that something decides how an edit is addressed:
 *
 *   order    the pronumber. Ambiguous the moment an order appears on two rows, so
 *            these switch off when a one-to-many column duplicates orders.
 *   leg      the pronumber plus a leg role. An order has one originate leg, so a
 *            duplicated order still addresses the same leg — no ambiguity.
 *   invoice  the invoice's own recno. A row here IS an invoice, so a repeated
 *            pronumber is the normal case rather than a problem: it is how an order
 *            with several accessorials shows up.
 *
 * That distinction is why a grid with an expanding invoice column can still edit the
 * invoice columns while the order columns sit read-only.
 *
 * The vocabulary itself comes from the backend, which mirrors the allowlist deciding
 * where /pro shows an "apply to all" checkbox for order fields, and hand-picks the leg
 * and invoice fields.
 */

/** What GET /orders/change-all/grid-fields publishes per editable column. */
export interface GridFieldSpec {
  role: 'yn' | 'number' | 'date' | 'datetime' | 'text' | 'select';
  scope: 'order' | 'leg' | 'invoice' | 'mainInvoice';
  /** The underlying column the save writes. */
  column: string;
  /** Leg scope only: which leg to target. */
  legRole?: string;
  /** A SECOND component permission this field needs on /pro, beyond order-lc-fields
   *  (Railbill, the weight group). Absent for everything else. */
  component?: string;
  /** `select` role only: the exact options /pro offers, so a bulk edit cannot put a
   *  value in that the form would refuse. A blank entry is a real choice.
   *
   *  {value, label} because the two diverge on a roster field: `csr` stores a username
   *  and displays a person's name. Resolved server-side from the field definition's
   *  `edit_source`, so the save validates against the very same list. */
  options?: { value: string; label: string }[];
  /** Templates binding only: 3 = always editable, 2 = editable only while the template
   *  is UNAPPROVED. Gated per ROW, since a grid can show both at once. */
  templateState?: 2 | 3;
}

export type GridFieldMap = Record<string, GridFieldSpec>;

/** The grid column carrying an invoice's own recno — the key for an invoice edit. */
export const INVOICE_RECNO_FIELD = 'anyinv_recno';

/** Search columns whose edit is addressed by the invoice recno. Adding any of these
 *  makes the search fetch the recno too, so the key is on the row without the user
 *  having to know they need a second column. Kept in step with the backend's
 *  INVOICE_FIELDS by a test. */
export const INVOICE_EDITABLE_FIELDS: ReadonlySet<string> = new Set([
  'anyinv_invoicecomments',
  'anyinv_billtorefno',
]);

const YN_OPTIONS = [
  { value: 'Y', label: 'Yes' },
  { value: 'N', label: 'No' },
];

/** The ordersall column behind a grid column id. Identity for all but the handful of
 *  names the dynamic-column mapping renames. */
export const columnForGridId = (columnId: string): string => getDbFieldName(columnId);

/**
 * The legacy DB column behind a grid column id, by the convention the order form
 * already uses (useOrderFieldDisplay.toDbName): the mismatch map first, then plain
 * lowercase, because the legacy columns are all-lowercase with no separators.
 *
 * This matters because the two halves of the grid name columns differently. A
 * picker-added column IS the fieldname (`masterpro`), but 27 of the 39 STATIC PRO
 * columns are camelCase display keys (`masterPro`, `isDrop`, `moveType`) — so matching
 * a published lowercase key on the id alone silently missed every editable field that
 * happens to be a built-in column.
 */
const toDbColumn = (columnId: string): string => toDbName(columnId);

/** The spec for a grid column, or undefined. Tried in order of how specific the name
 *  is: the id itself (leg/invoice columns and picker-added fieldnames are published
 *  verbatim), the dynamic-column rename, then the legacy lowercase form. */
export function specFor(columnId: string, fields: GridFieldMap): GridFieldSpec | undefined {
  return fields[columnId]
    ?? fields[columnForGridId(columnId)]
    ?? fields[toDbColumn(columnId)];
}

/**
 * The bulk-edit widget for a grid column, or null when it may not be edited here.
 *
 * `ordersDisabled` switches off ORDER-scoped fields only — used when a one-to-many
 * column has duplicated orders, which leaves leg and invoice fields perfectly
 * addressable. An empty map (still loading, or the call failed) yields no affordance
 * anywhere: better a read-only grid than cells that fail on save.
 */
export function gridEditMetaFor(
  columnId: string,
  fields: GridFieldMap,
  opts: {
    ordersDisabled?: boolean;
    hasInvoiceKey?: boolean;
    /** Component ids the user may write. A field naming a component absent from this
     *  set is withheld, so the save is never the first time they are refused. */
    allowedComponents?: ReadonlySet<string>;
  } = {},
): BulkEditMeta | null {
  const spec = specFor(columnId, fields);
  if (!spec) return null;
  if (spec.component && !opts.allowedComponents?.has(spec.component)) return null;
  // mainInvoice is keyed by pronumber like an order field, so duplicated orders make
  // it ambiguous in exactly the same way.
  if ((spec.scope === 'order' || spec.scope === 'mainInvoice') && opts.ordersDisabled) return null;
  // An invoice edit is addressed by recno. Without that column on the row there is no
  // key, so the cell must not pretend to be editable.
  if (spec.scope === 'invoice' && !opts.hasInvoiceKey) return null;

  switch (spec.role) {
    case 'yn':
      return { type: 'dropdown', options: YN_OPTIONS };
    case 'number':
      return { type: 'number' };
    case 'date':
    case 'datetime':
      // Both edit as a date. A datetime column keeps whatever time it holds unless the
      // user picks a new date, and the change-all writer normalises either form.
      return { type: 'date' };
    case 'text':
      return { type: 'text' };
    case 'select':
      // The options come from the backend, which resolves /pro's own list — no second
      // source to drift, and no fetch here. Without them there is nothing to offer,
      // which is the intended state for a roster field whose lookup failed: read-only
      // beats a free-text box that would bypass the roster.
      return spec.options?.length
        ? {
            type: 'dropdown',
            options: spec.options.map((o) => ({ value: o.value, label: o.label || '—' })),
          }
        : null;
    default:
      return null;
  }
}

/**
 * The key a staged edit is filed under, unique per addressable record.
 *
 * Prefixed by scope so an order edit and an invoice edit on the SAME row never
 * collide — which they would if both keyed on the pronumber alone.
 */
export function stagingKey(spec: GridFieldSpec, row: Record<string, unknown>): string | null {
  const pro = String(row?.pronumber ?? '').trim();
  if (spec.scope === 'invoice') {
    const recno = String(row?.[INVOICE_RECNO_FIELD] ?? '').trim();
    return recno ? `invoice:${recno}:${pro}` : null;
  }
  if (!pro) return null;
  if (spec.scope === 'leg') return `leg:${pro}:${spec.legRole}`;
  // Filed apart from the order scope so an order edit and a main-invoice edit on the
  // same row do not overwrite each other; the server merges them into one save.
  if (spec.scope === 'mainInvoice') return `mainv:${pro}`;
  return `order:${pro}`;
}

/** The three arms of the save payload, rebuilt from staged keys. */
export interface GridEditPayload {
  items: { pronumber: string; values: Record<string, unknown> }[];
  legs: { pronumber: string; legRole: string; values: Record<string, unknown> }[];
  invoices: { recno: number; pronumber: string; values: Record<string, unknown> }[];
  mainInvoices: { pronumber: string; values: Record<string, unknown> }[];
}

/** Split staged edits back into the arm each belongs to. The key carries everything
 *  needed, so no row lookup is required at save time — rows may have been filtered
 *  away or refetched since the edit was made. */
export function buildPayload(
  staged: { recno: number | string; values: Record<string, unknown> }[],
): GridEditPayload {
  const out: GridEditPayload = { items: [], legs: [], invoices: [], mainInvoices: [] };
  for (const entry of staged) {
    const [scope, a, b] = String(entry.recno).split(':');
    if (scope === 'order') out.items.push({ pronumber: a, values: entry.values });
    else if (scope === 'mainv') out.mainInvoices.push({ pronumber: a, values: entry.values });
    else if (scope === 'leg') out.legs.push({ pronumber: a, legRole: b, values: entry.values });
    else if (scope === 'invoice') {
      out.invoices.push({ recno: Number(a), pronumber: b ?? '', values: entry.values });
    }
  }
  return out;
}

/**
 * Pros appearing on more than one row.
 *
 * Only ORDER-scoped edits care: those are addressed by pronumber, so two rows for one
 * order means an edit that cannot be placed. Leg edits resolve through the same order
 * to the same leg, and invoice edits are addressed by their own recno, so both stay
 * available while this is non-empty.
 */
export function duplicatedPros(rows: { pronumber?: unknown }[]): string[] {
  const seen = new Set<string>();
  const dupes = new Set<string>();
  for (const row of rows) {
    const pro = String(row?.pronumber ?? '').trim();
    if (!pro) continue;
    if (seen.has(pro)) dupes.add(pro);
    seen.add(pro);
  }
  return [...dupes];
}

/**
 * The staged keys belonging to any of `pros` — what to keep staged after a save so a
 * failed record can be fixed and retried while the successful ones clear.
 *
 * The key embeds the pronumber for every scope, so this needs no row lookup. It is
 * deliberately per-ORDER rather than per-record: the save result identifies a failure
 * by pronumber, and when an order has two accessorials and one fails, both stay
 * staged. Re-saving the one that already applied is a no-op (the writer diffs against
 * the stored value), which is a better trade than silently dropping an edit.
 */
export function keysForPros(keys: string[], pros: Iterable<string>): string[] {
  const wanted = new Set([...pros].map(String));
  return keys.filter((key) => {
    const [scope, a, b] = key.split(':');
    return wanted.has(scope === 'invoice' ? (b ?? '') : a);
  });
}

/**
 * Columns this ORDER cannot edit, from the config-driven display rules.
 *
 * /pro's contract is "editable = may-write AND shown AND not-readonly", and which
 * fields those are depends on the order's movetype. The grid applies the movetype half
 * per row so a cell that would be refused never looks editable; the INVOICED half
 * needs the order's invoice state, which the row does not carry, so the server is the
 * one that enforces it.
 *
 * `hidden` and `readonly` collapse — the grid has no hidden state, and a field the
 * order form will not show is not one to bulk-write.
 */
export interface DisplayRule {
  targetType?: string;
  target: string;
  scope: string;
  behavior: string;
}

export function displayBlockedForRow(
  rules: readonly DisplayRule[],
  movetype: unknown,
): ReadonlySet<string> {
  const mt = String(movetype ?? '').trim().toUpperCase();
  const out = new Set<string>();
  for (const rule of rules) {
    // The fetch returns tab rules too, whose `target` is a tab id — matching one
    // against a column name would block a field for no reason.
    if (rule.targetType && rule.targetType !== 'field') continue;
    const scope = String(rule.scope ?? '').trim().toUpperCase();
    if (scope !== 'ALL' && scope !== mt) continue;
    if (rule.behavior === 'hidden' || rule.behavior === 'readonly') out.add(rule.target);
  }
  return out;
}

/**
 * Does this row carry an unsaved edit, in any scope?
 *
 * Drives the row highlight. A single amber cell is easy to lose in a wide grid that
 * scrolls sideways — the row it belongs to may be the only part still on screen — so
 * the row is tinted as well.
 *
 * Checks every scope the row can be keyed under rather than just the order, or a
 * staged invoice comment would leave its row unmarked.
 */
export function rowHasStagedEdit(
  row: Record<string, unknown>,
  editedKeys: ReadonlySet<string>,
): boolean {
  const pro = String(row?.pronumber ?? '').trim();
  if (pro) {
    if (editedKeys.has(`order:${pro}`) || editedKeys.has(`mainv:${pro}`)) return true;
    for (const key of editedKeys) {
      if (key.startsWith(`leg:${pro}:`)) return true;
    }
  }
  const recno = String(row?.[INVOICE_RECNO_FIELD] ?? '').trim();
  return !!recno && editedKeys.has(`invoice:${recno}:${pro}`);
}

/** The staging-key prefix a scope files its edits under. Lets a caller select the
 *  edits belonging to ONE grid column without catching a same-named column in another
 *  scope — `mainv_invoicecomments` and `anyinv_invoicecomments` both write
 *  `invoicecomments`, and they are different records. */
export function keyPrefixForScope(scope: GridFieldSpec['scope']): string {
  if (scope === 'leg') return 'leg:';
  if (scope === 'invoice') return 'invoice:';
  if (scope === 'mainInvoice') return 'mainv:';
  return 'order:';
}
